import { create } from 'zustand';
import queryClient from '../services/queryClient';
import useChatStore from './useChatStore';
import { THREAD_DEFAULT_LANGUAGE_MODEL, TThread } from '@core/shared/types';
import i18n from 'i18next';
import { MESSAGESTORE_KEY } from './useMessageStore';
import { logger } from '@shared/logger';
import { handleError } from '@shared/handleError'; // Import the logger

export const OPTIMISTIC_HISTORY_ID = 'OPTIMISTIC_HISTORY_ID';
export const HISTORYSTORE_KEY = 'HistoryStore'; // will be used as the key for the loading state, error state and caching prefix

type THistoryStore = {
    historyThreads: TThread[] | null;

    /**
     * Fetches the history threads for the current user or tenant.
     * @returns {Promise<void>} - A promise that resolves when the threads are fetched.
     */
    getHistoryThreads: (searchQuery?: string) => Promise<void>;

    /**
     * Deletes a specific thread from the history.
     * @param {string} threadId - The ID of the thread to delete.
     * @returns {Promise<void>} - A promise that resolves when the thread is deleted.
     */
    deleteThread: (threadId: string) => Promise<void>;

    /**
     * Clears all history threads from the store.
     * @returns {void}
     */
    clearHistoryThreads: () => void;

    /**
     * Adds an optimistic history thread to the store.
     * @returns {void}
     */
    addHistoryThreadOptimistic: () => void;

    /**
     * Invalidates the cache for history threads and fetches them again.
     * @returns {Promise<void>} - A promise that resolves when the cache is invalidated and history threads are fetched.
     */
    invalidateHistoryCache: () => Promise<void>;

    /**
     * Revives an optimistic history thread by updating its ID and subject.
     * @param {string} thread_id - The ID of the thread to update.
     * @param {string} thread_subject - The new subject of the thread.
     * @returns {void}
     */
    reviveOptimisticThread: (thread_id: string, thread_subject: string) => void;

    /**
     * Prunes (removes) any optimistic history threads from the store.
     * @returns {void}
     */
    pruneOptimisticThread: () => void;

    /**
     * Removes a specific optimistic history thread from the store.
     * @param {string} thread_id - The ID of the thread to remove.
     * @returns {void}
     */
    removeHistoryThreadOptimistic: (thread_id: string) => void;
};

const useHistoryStore = create<THistoryStore>()((set, get) => ({
    historyThreads: null,

    /**
     * Fetches history threads from the server for the current route configuration.
     * Logs the process and updates the store with the fetched threads.
     * @returns {Promise<void>}
     */
    getHistoryThreads: async (searchQuery?: string): Promise<void> => {
        const routeConfig = useChatStore.getState().routeConfig;
        logger.log(HISTORYSTORE_KEY, 1, 'getHistoryThreads called'); // Logging function call

        try {
            const queryKey = [HISTORYSTORE_KEY, routeConfig.routePath, searchQuery?.trim() ?? '']; // Create a unique query key
            useChatStore.getState().setLoadingState(HISTORYSTORE_KEY, true);
            logger.log(HISTORYSTORE_KEY, 1, 'Fetching history threads with queryKey:', queryKey); // Log query key
            // Cancel any ongoing queries related to the history
            await queryClient.cancelQueries({ queryKey });

            // Fetch the conversations with the current historyConversations and tenant as part of the queryKey
            const agentId = useChatStore.getState().aiAgent?.id || '';
            const historyThreads = await queryClient.fetchQuery({
                queryKey,
                queryFn: () => routeConfig.apiClient.getHistoryThreads(agentId, searchQuery),
            });

            logger.log(HISTORYSTORE_KEY, 1, 'Fetched history threads:', historyThreads); // Log fetched threads
            set({ historyThreads });
        } catch (error) {
            await handleError(error, HISTORYSTORE_KEY);

            set({ historyThreads: null });
        } finally {
            useChatStore.getState().setError(MESSAGESTORE_KEY, null);
            useChatStore.getState().setLoadingState(HISTORYSTORE_KEY, false);
            logger.log(HISTORYSTORE_KEY, 1, 'Finished fetching history threads'); // Log completion
        }
    },

    /**
     * Clears all the history threads from the store.
     * @returns {void}
     */
    clearHistoryThreads: (): void => {
        logger.log(HISTORYSTORE_KEY, 1, 'clearHistoryThreads called'); // Log function call
        set({ historyThreads: null });
    },

    /**
     * Adds an optimistic history thread with a placeholder ID and subject.
     * This thread will be updated later when the actual data is fetched.
     * @returns {void}
     */
    addHistoryThreadOptimistic: (): void => {
        if (useChatStore.getState().isPreview) return;

        const optimisticThread: TThread = {
            thread_id: OPTIMISTIC_HISTORY_ID,
            thread_subject: i18n.t('newChat'),
            language_model: THREAD_DEFAULT_LANGUAGE_MODEL,
            updated: new Date().toISOString(),
            created: new Date().toISOString(),
        };

        logger.log(
            HISTORYSTORE_KEY,
            1,
            'addOptimisticHistoryThread called with:',
            optimisticThread,
        ); // Log optimistic thread creation
        // Add the optimistic thread from OPTIMISTIC_PLACEHOLDER_HISTORY_THREAD to the historyThreads
        set((state) => ({
            historyThreads: state.historyThreads
                ? [optimisticThread, ...state.historyThreads]
                : [optimisticThread],
        }));
    },

    /**
     * Revives an optimistic thread by updating it with the given thread ID and subject.
     * @param {string} thread_id - The new ID of the thread to update.
     * @param {string} thread_subject - The new subject of the thread.
     * @returns {void}
     */
    reviveOptimisticThread: (thread_id: string, thread_subject: string): void => {
        logger.log(HISTORYSTORE_KEY, 1, 'reviveOptimisticThread called with thread_id:', thread_id); // Log revival of optimistic thread
        set((state) => ({
            // Revive the optimistic thread with the new thread_id and thread_subject
            historyThreads: state.historyThreads?.map((thread) => {
                if (thread.thread_id === OPTIMISTIC_HISTORY_ID) {
                    return { ...thread, thread_id, thread_subject, new: true };
                }
                return thread;
            }),
        }));
    },

    /**
     * Prunes (removes) any optimistic threads from the history store.
     * @returns {void}
     */
    pruneOptimisticThread: (): void => {
        logger.log(HISTORYSTORE_KEY, 1, 'pruneOptimisticThread called'); // Log pruning of optimistic threads
        set((state) => ({
            historyThreads: state.historyThreads?.filter(
                (ht) => ht.thread_id !== OPTIMISTIC_HISTORY_ID,
            ),
        }));
    },

    /**
     * Removes a specific optimistic thread by its ID.
     * @param {string} thread_id - The ID of the thread to remove.
     * @returns {void}
     */
    removeHistoryThreadOptimistic: (thread_id: string): void => {
        logger.log(
            HISTORYSTORE_KEY,
            1,
            'removeHistoryThreadOptimistic called with thread_id:',
            thread_id,
        ); // Log removal of optimistic thread
        set((state) => ({
            historyThreads: state.historyThreads?.filter((ht) => ht.thread_id !== thread_id),
        }));
    },

    /**
     * Deletes a thread from the history by its ID and re-fetches the updated history threads.
     * @param {string} threadId - The ID of the thread to delete.
     * @returns {Promise<void>}
     */
    deleteThread: async (threadId: string): Promise<void> => {
        //the user is not allowed to delete the thread if the threadId is the optimistic thread id
        if (threadId === OPTIMISTIC_HISTORY_ID) {
            logger.log(HISTORYSTORE_KEY, 1, 'User tried to delete optimistic thread'); // Log optimistic thread deletion attempt
            return;
        }

        // we don't need a loader here, because nobody is waiting for the result
        const routeConfig = useChatStore.getState().routeConfig;

        const queryKey = [HISTORYSTORE_KEY, routeConfig.routePath];
        logger.log(HISTORYSTORE_KEY, 1, 'deleteThread called with threadId:', threadId); // Log thread deletion initiation
        get().removeHistoryThreadOptimistic(threadId);

        //function will be used later to re-fetch the history threads
        const reFetchHistoryThreads = async () => {
            // Re-fetch the history threads
            await queryClient.refetchQueries({ queryKey });
            // Refresh the history threads in the store
            await get().getHistoryThreads();
        };

        try {
            await routeConfig.apiClient.deleteThread(threadId);
            logger.log(HISTORYSTORE_KEY, 1, 'Successfully deleted thread:', threadId); // Log successful deletion
        } catch (error) {
            await handleError(error, HISTORYSTORE_KEY);
            await reFetchHistoryThreads();
        } finally {
            // Ensure the history threads are refetched after the deletion attempt
            useChatStore.getState().setError(MESSAGESTORE_KEY, null);
            await reFetchHistoryThreads();
            logger.log(
                HISTORYSTORE_KEY,
                1,
                'Finished deleting thread and re-fetching history threads',
            ); // Log completion
        }
    },

    /**
     * Invalidates the cache for the history threads and fetches them again.
     * @returns {Promise<void>}
     */
    invalidateHistoryCache: async (): Promise<void> => {
        const queryKey = [HISTORYSTORE_KEY, useChatStore.getState().routeConfig.routePath];

        try {
            // Invalidate the cache for the history threads
            await queryClient.invalidateQueries({ queryKey });
            logger.log(HISTORYSTORE_KEY, 1, 'Invalidated history cache for queryKey:', queryKey); // Log cache invalidation
            // Fetch the history threads again to ensure the state is updated
            await get().getHistoryThreads();
        } catch (error) {
            await handleError(error, HISTORYSTORE_KEY);
        }
    },
}));

export default useHistoryStore;
